import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Block as BlockIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import CreatePublicItineraryModal from '../components/CreatePublicItineraryModal';
import ProfileDashboard from '../components/ProfileDashboard';
import config from '../config/config';

// Configure axios defaults
axios.defaults.baseURL = config.API_URL;
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AdminDashboard = () => {
  const [tab, setTab] = useState(0);
  const [operators, setOperators] = useState([]);
  const [pendingOperators, setPendingOperators] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [openCreateItineraryModal, setOpenCreateItineraryModal] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [operatorMetrics, setOperatorMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentReports, setPaymentReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportDetailsDialog, setReportDetailsDialog] = useState(false);
  const [reportDetails, setReportDetails] = useState(null);
  const [newOperator, setNewOperator] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
    password: '',
    firstName: '',
    lastName: ''
  });

  useEffect(() => {
    fetchOperators();
    fetchPendingOperators();
  }, []);

  const fetchOperators = async () => {
    try {
      const response = await axios.get('/admin/operators');
      setOperators(response.data);
    } catch (error) {
      console.error('Error fetching operators:', error);
      toast.error('Failed to load operators');
    }
  };

  const fetchPendingOperators = async () => {
    try {
      const response = await axios.get('/admin/operators/requests');
      setPendingOperators(response.data);
    } catch (error) {
      console.error('Error fetching pending operators:', error);
      toast.error('Failed to load pending requests');
    }
  };

  const fetchOperatorMetrics = async (operatorId) => {
    setLoadingMetrics(true);
    try {
      const response = await axios.get(`/admin/operators/${operatorId}/metrics`);
      setOperatorMetrics(response.data);
    } catch (error) {
      console.error('Error fetching operator metrics:', error);
      toast.error('Failed to load operator metrics');
    } finally {
      setLoadingMetrics(false);
    }
  };

  const fetchOperatorPaymentReports = async (operatorId) => {
    setLoadingReports(true);
    try {
      const response = await axios.get(`/admin/operators/${operatorId}/payment-reports`);
      setPaymentReports(response.data);
    } catch (error) {
      console.error('Error fetching payment reports:', error);
      toast.error('Failed to load payment reports');
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchReportDetails = async (operatorId, date) => {
    try {
      const response = await axios.get(`/admin/operators/${operatorId}/payment-reports/${date}`);
      setReportDetails(response.data);
      setReportDetailsDialog(true);
    } catch (error) {
      console.error('Error fetching report details:', error);
      toast.error('Failed to load report details');
    }
  };

  const handleCreateOperator = async () => {
    try {
      await axios.post('/admin/operators', newOperator);
      toast.success('Operator created successfully');
      setCreateDialog(false);
      setNewOperator({
        name: '',
        email: '',
        phone: '',
        description: '',
        password: '',
        firstName: '',
        lastName: ''
      });
      fetchOperators();
    } catch (error) {
      console.error('Error creating operator:', error);
      toast.error('Failed to create operator');
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(`/admin/operators/${selectedOperator.id}/approve`, {
        temporaryPassword
      });
      toast.success('Operator approved successfully');
      setApprovalDialog(false);
      setTemporaryPassword('');
      fetchOperators();
      fetchPendingOperators();
    } catch (error) {
      console.error('Error approving operator:', error);
      toast.error('Failed to approve operator');
    }
  };

  const handleReject = async (operatorId) => {
    if (window.confirm('Are you sure you want to reject this operator?')) {
      try {
        await axios.post(`/admin/operators/${operatorId}/reject`);
        toast.success('Operator rejected successfully');
        fetchPendingOperators();
      } catch (error) {
        console.error('Error rejecting operator:', error);
        toast.error('Failed to reject operator');
      }
    }
  };

  const handleDeactivate = async (operatorId) => {
    if (window.confirm('Are you sure you want to deactivate this operator?')) {
      try {
        await axios.post(`/admin/operators/${operatorId}/deactivate`);
        toast.success('Operator deactivated successfully');
        fetchOperators();
      } catch (error) {
        console.error('Error deactivating operator:', error);
        toast.error('Failed to deactivate operator');
      }
    }
  };

  const handleViewMetrics = (operator) => {
    setSelectedOperator(operator);
    fetchOperatorMetrics(operator.id);
    fetchOperatorPaymentReports(operator.id);
  };

  const handleUpdateReportStatus = async (reportId, status) => {
    try {
      await axios.put(`/admin/payment-reports/${reportId}/status`, { status });
      toast.success('Report status updated successfully');
      // Refresh both the report details and payment reports list
      if (selectedOperator && reportDetails?.date) {
        fetchReportDetails(selectedOperator.id, reportDetails.date);
        fetchOperatorPaymentReports(selectedOperator.id);
      }
    } catch (error) {
      console.error('Error updating report status:', error);
      toast.error('Failed to update report status');
    }
  };

  const handleMarkAllAsProcessed = async () => {
    const pendingTransactions = reportDetails.transactions.filter(t => t.status === 'pending');
    if (pendingTransactions.length === 0) {
      toast.info('No pending transactions to process');
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const transaction of pendingTransactions) {
      try {
        await axios.put(`/admin/payment-reports/${transaction.id}/status`, { status: 'processed' });
        successCount++;
      } catch (error) {
        console.error('Error processing transaction:', error);
        failCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`Successfully processed ${successCount} transactions`);
    }
    if (failCount > 0) {
      toast.error(`Failed to process ${failCount} transactions`);
    }

    // Refresh the report details and payment reports
    if (selectedOperator && reportDetails?.date) {
      fetchReportDetails(selectedOperator.id, reportDetails.date);
      fetchOperatorPaymentReports(selectedOperator.id);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getReportStatus = (transactions) => {
    if (!transactions || transactions.length === 0) return 'pending';
    return transactions.every(t => t.status === 'processed') ? 'processed' : 'pending';
  };

  const areAllTransactionsProcessed = (transactions) => {
    return transactions && transactions.length > 0 && transactions.every(t => t.status === 'processed');
  };

  const filteredOperators = operators.filter(operator => {
    const matchesSearch = operator.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         operator.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' ? true :
                         (statusFilter === 'active' ? operator.User?.isActive : !operator.User?.isActive);
    return matchesSearch && matchesStatus;
  });

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7f9' }}>
      <Navbar hideFilters />
      <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Admin Dashboard
          </Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialog(true)}
              sx={{ mr: 2 }}
            >
              Create Operator
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateItineraryModal(true)}
            >
              Create Public Itinerary
            </Button>
          </Box>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)}>
            <Tab label="All Operators" />
            <Tab label={`Pending Requests (${pendingOperators.length})`} />
            <Tab label="Reports" icon={<AssessmentIcon />} iconPosition="start" />
          </Tabs>
        </Paper>

        {tab === 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {operators.map((operator) => (
                  <TableRow key={operator.id}>
                    <TableCell>{operator.name}</TableCell>
                    <TableCell>{operator.email}</TableCell>
                    <TableCell>{operator.phone}</TableCell>
                    <TableCell>
                      <Chip
                        label={operator.User?.isActive ? 'Active' : 'Inactive'}
                        color={operator.User?.isActive ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleDeactivate(operator.id)}
                        color="error"
                        title="Deactivate"
                      >
                        <BlockIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {tab === 1 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingOperators.map((operator) => (
                  <TableRow key={operator.id}>
                    <TableCell>{operator.name}</TableCell>
                    <TableCell>{operator.email}</TableCell>
                    <TableCell>{operator.phone}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setSelectedOperator(operator);
                          setApprovalDialog(true);
                        }}
                        color="success"
                        title="Approve"
                      >
                        <CheckIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleReject(operator.id)}
                        color="error"
                        title="Reject"
                      >
                        <CloseIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {tab === 2 && (
          <Box>
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
              <TextField
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ flexGrow: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: 120 }}>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOperators.map((operator) => (
                    <React.Fragment key={operator.id}>
                      <TableRow>
                        <TableCell>{operator.name}</TableCell>
                        <TableCell>{operator.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={operator.User?.isActive ? 'Active' : 'Inactive'}
                            color={operator.User?.isActive ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            startIcon={<AssessmentIcon />}
                            onClick={() => handleViewMetrics(operator)}
                            size="small"
                          >
                            View Metrics
                          </Button>
                        </TableCell>
                      </TableRow>
                      {operatorMetrics && selectedOperator?.id === operator.id && (
                        <TableRow>
                          <TableCell colSpan={4}>
                            {loadingMetrics ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                              </Box>
                            ) : (
                              <Box sx={{ p: 3 }}>
                                <ProfileDashboard
                                  stats={{
                                    totalTrips: operatorMetrics.totalTours,
                                    placesVisited: operatorMetrics.nativeUsersBooked,
                                    hotelsBooked: operatorMetrics.guestUsersAdded
                                  }}
                                  labels={{
                                    totalTrips: 'Total Tours Created',
                                    placesVisited: 'Native Users Booked',
                                    hotelsBooked: 'Guest Users Added'
                                  }}
                                />
                                {loadingReports ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                    <CircularProgress />
                                  </Box>
                                ) : paymentReports.length > 0 ? (
                                  <Box sx={{ mt: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                      <ReceiptIcon sx={{ mr: 1, color: '#316C6E' }} />
                                      <Typography variant="h6">
                                        Payment Reports
                                      </Typography>
                                    </Box>
                                    <TableContainer component={Paper} sx={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                            <TableCell>Date</TableCell>
                                            <TableCell align="right">Transactions</TableCell>
                                            <TableCell align="right">Total Amount (DOP)</TableCell>
                                            <TableCell align="right">Management Fee (DOP)</TableCell>
                                            <TableCell align="right">Final Amount (DOP)</TableCell>
                                            <TableCell align="center">Status</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {paymentReports.map((report) => (
                                            <TableRow 
                                              key={report.date}
                                              sx={{ '&:hover': { bgcolor: '#f8f8f8' } }}
                                            >
                                              <TableCell>{report.date}</TableCell>
                                              <TableCell align="right">{report.transactionCount}</TableCell>
                                              <TableCell align="right" sx={{ color: '#316C6E', fontWeight: 500 }}>
                                                {formatAmount(report.totalAmount)}
                                              </TableCell>
                                              <TableCell align="right" sx={{ color: '#d32f2f' }}>
                                                {formatAmount(report.managementFee)}
                                              </TableCell>
                                              <TableCell align="right" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                                                {formatAmount(report.finalAmount)}
                                              </TableCell>
                                              <TableCell align="center">
                                                <Chip
                                                  size="small"
                                                  icon={report.status === 'processed' ? <CheckCircleIcon /> : <ScheduleIcon />}
                                                  label={report.status === 'processed' ? 'Processed' : 'Pending'}
                                                  color={report.status === 'processed' ? 'success' : 'warning'}
                                                  sx={{ minWidth: 100 }}
                                                />
                                              </TableCell>
                                              <TableCell align="right">
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  onClick={() => fetchReportDetails(selectedOperator.id, report.date)}
                                                  sx={{ 
                                                    borderColor: '#316C6E',
                                                    color: '#316C6E',
                                                    '&:hover': {
                                                      borderColor: '#254F51',
                                                      bgcolor: 'rgba(49, 108, 110, 0.04)'
                                                    }
                                                  }}
                                                >
                                                  View Details
                                                </Button>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </TableContainer>
                                  </Box>
                                ) : (
                                  <Box sx={{ mt: 3, textAlign: 'center', p: 3, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                    <ReceiptIcon sx={{ fontSize: 40, color: '#666', mb: 1 }} />
                                    <Typography color="textSecondary">
                                      No payment reports available
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Approval Dialog */}
        <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)}>
          <DialogTitle>Set Temporary Password</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Temporary Password"
              type="text"
              value={temporaryPassword}
              onChange={(e) => setTemporaryPassword(e.target.value)}
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
            <Button onClick={handleApprove} variant="contained" color="primary">
              Approve
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Operator Dialog */}
        <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Operator</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Business Name"
              value={newOperator.name}
              onChange={(e) => setNewOperator({ ...newOperator, name: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="First Name"
              value={newOperator.firstName}
              onChange={(e) => setNewOperator({ ...newOperator, firstName: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Last Name"
              value={newOperator.lastName}
              onChange={(e) => setNewOperator({ ...newOperator, lastName: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newOperator.email}
              onChange={(e) => setNewOperator({ ...newOperator, email: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Phone"
              value={newOperator.phone}
              onChange={(e) => setNewOperator({ ...newOperator, phone: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={newOperator.description}
              onChange={(e) => setNewOperator({ ...newOperator, description: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={newOperator.password}
              onChange={(e) => setNewOperator({ ...newOperator, password: e.target.value })}
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setCreateDialog(false);
              setNewOperator({
                name: '',
                email: '',
                phone: '',
                description: '',
                password: '',
                firstName: '',
                lastName: ''
              });
            }}>Cancel</Button>
            <Button onClick={handleCreateOperator} variant="contained" color="primary">
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Public Itinerary Modal */}
        <CreatePublicItineraryModal
          open={openCreateItineraryModal}
          onClose={() => setOpenCreateItineraryModal(false)}
        />

        {/* Report Details Dialog */}
        <Dialog
          open={reportDetailsDialog}
          onClose={() => setReportDetailsDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: '#f8f8f8',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <ReceiptIcon sx={{ color: '#316C6E' }} />
            Payment Report Details - {reportDetails?.date}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {reportDetails && (
              <>
                <TableContainer component={Paper} sx={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)', mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell>Customer</TableCell>
                        <TableCell>Tour</TableCell>
                        <TableCell>Tour Date</TableCell>
                        <TableCell>Booking Date</TableCell>
                        <TableCell align="right">Amount (DOP)</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reportDetails.transactions.map((transaction) => (
                        <TableRow 
                          key={transaction.id}
                          sx={{ '&:hover': { bgcolor: '#f8f8f8' } }}
                        >
                          <TableCell>{transaction.customerName}</TableCell>
                          <TableCell>{transaction.tourName}</TableCell>
                          <TableCell>{transaction.tourDate}</TableCell>
                          <TableCell>{transaction.bookingDate}</TableCell>
                          <TableCell align="right" sx={{ color: '#316C6E', fontWeight: 500 }}>
                            {formatAmount(transaction.amount)}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              icon={transaction.status === 'processed' ? <CheckCircleIcon /> : <ScheduleIcon />}
                              label={transaction.status === 'processed' ? 'Processed' : 'Pending'}
                              color={transaction.status === 'processed' ? 'success' : 'warning'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {transaction.status === 'pending' && (
                              <Button
                                size="small"
                                startIcon={<CheckCircleIcon />}
                                onClick={() => handleUpdateReportStatus(transaction.id, 'processed')}
                                sx={{ color: '#2e7d32' }}
                              >
                                Mark as Processed
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Paper sx={{ p: 2, bgcolor: '#f8f8f8', borderRadius: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: '#316C6E', fontWeight: 500 }}>
                    Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">Total Amount</Typography>
                        <Typography variant="h6" sx={{ color: '#316C6E' }}>
                          DOP {formatAmount(reportDetails.summary.totalAmount)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">Management Fee (10%)</Typography>
                        <Typography variant="h6" sx={{ color: '#d32f2f' }}>
                          DOP {formatAmount(reportDetails.summary.managementFee)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="body2" color="textSecondary">Final Amount</Typography>
                        <Typography variant="h6" sx={{ color: '#2e7d32' }}>
                          DOP {formatAmount(reportDetails.summary.finalAmount)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: '#f8f8f8' }}>
            <Button
              onClick={handleMarkAllAsProcessed}
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              disabled={reportDetails?.transactions?.every(t => t.status === 'processed')}
              sx={{ mr: 'auto' }}
            >
              Mark All as Processed
            </Button>
            <Button 
              onClick={() => setReportDetailsDialog(false)}
              variant="outlined"
              sx={{ 
                borderColor: '#316C6E',
                color: '#316C6E',
                '&:hover': {
                  borderColor: '#254F51',
                  bgcolor: 'rgba(49, 108, 110, 0.04)'
                }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminDashboard; 